/*
=========================================================
ASSABOOR ENTERPRISE
CART BUNDLE ENGINE v3.0
File: assets/ae-cart-bundle.js
=========================================================
*/

(() => {
  'use strict';

  const SELECTORS = {
    bundle: '[data-ae-cart-bundle]',
    product: '[data-ae-bundle-product]',
    checkbox: '[data-ae-bundle-checkbox]',
    variantSelect: '[data-ae-bundle-variant-select]',
    addButton: '[data-ae-add-bundle]',
    productPrice: '[data-ae-bundle-product-price]',
    productCompare: '[data-ae-bundle-product-compare]',
    total: '[data-ae-bundle-total]',
    compare: '[data-ae-bundle-compare]',
    saving: '[data-ae-bundle-saving]',
    selectedCount: '[data-ae-bundle-selected-count]',
    message: '[data-ae-bundle-message]',
    drawerShell: '[data-ae-cart-drawer-shell]',
    drawer: '[data-ae-cart-drawer]',
    overlay: '[data-ae-cart-drawer-overlay]'
  };

  class AECartBundle {
    constructor() {
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleCartUpdated = this.handleCartUpdated.bind(this);

      this.bindEvents();
      this.refreshAllBundles();
    }

    bindEvents() {
      document.addEventListener('click', this.handleClick);
      document.addEventListener('change', this.handleChange);
      document.addEventListener('ae:cart:updated', this.handleCartUpdated);
      document.addEventListener('cart:updated', this.handleCartUpdated);
    }

    handleCartUpdated() {
      this.refreshAllBundles();
    }

    handleClick(event) {
      const button = event.target.closest(SELECTORS.addButton);

      if (!button) return;

      event.preventDefault();
      this.addBundle(button);
    }

    handleChange(event) {
      const checkbox = event.target.closest(SELECTORS.checkbox);
      const variantSelect = event.target.closest(SELECTORS.variantSelect);

      if (!checkbox && !variantSelect) return;

      const product = event.target.closest(SELECTORS.product);
      const bundle = event.target.closest(SELECTORS.bundle);

      if (!product || !bundle) return;

      if (checkbox) {
        product.classList.toggle('is-selected', checkbox.checked);
      }

      if (variantSelect) {
        this.updateProductFromVariant(product, variantSelect);
      }

      this.updateBundle(bundle);
    }

    refreshAllBundles() {
      document
        .querySelectorAll(SELECTORS.bundle)
        .forEach((bundle) => {
          this.updateBundle(bundle);
        });
    }

    getProducts(bundle) {
      return [
        ...bundle.querySelectorAll(SELECTORS.product)
      ];
    }

    getSelectedProducts(bundle) {
      return this.getProducts(bundle).filter((product) => {
        const checkbox = product.querySelector(
          SELECTORS.checkbox
        );

        if (checkbox) {
          return checkbox.checked;
        }

        return product.classList.contains(
          'is-selected'
        );
      });
    }

    getSelectedOption(select) {
      if (!select || select.selectedIndex < 0) {
        return null;
      }

      return select.options[select.selectedIndex] || null;
    }

    getProductData(product) {
      const variantSelect = product.querySelector(
        SELECTORS.variantSelect
      );

      if (variantSelect) {
        const option = this.getSelectedOption(
          variantSelect
        );

        return {
          variantId: Number.parseInt(
            option?.value || '0',
            10
          ),

          price: Number.parseInt(
            option?.dataset.price || '0',
            10
          ),

          comparePrice: Number.parseInt(
            option?.dataset.comparePrice ||
            option?.dataset.price ||
            '0',
            10
          ),

          available: Boolean(
            option && !option.disabled
          )
        };
      }

      return {
        variantId: Number.parseInt(
          product.dataset.variantId || '0',
          10
        ),

        price: Number.parseInt(
          product.dataset.price || '0',
          10
        ),

        comparePrice: Number.parseInt(
          product.dataset.comparePrice ||
          product.dataset.price ||
          '0',
          10
        ),

        available:
          product.dataset.available !== 'false'
      };
    }

    updateProductFromVariant(product, select) {
      const option = this.getSelectedOption(select);

      if (!option) return;

      product.dataset.variantId =
        option.value || '';

      product.dataset.price =
        option.dataset.price || '0';

      product.dataset.comparePrice =
        option.dataset.comparePrice ||
        option.dataset.price ||
        '0';

      product.dataset.available =
        option.disabled ? 'false' : 'true';

      const priceElement = product.querySelector(
        SELECTORS.productPrice
      );

      const compareElement = product.querySelector(
        SELECTORS.productCompare
      );

      const price = Number.parseInt(
        option.dataset.price || '0',
        10
      );

      const comparePrice = Number.parseInt(
        option.dataset.comparePrice ||
        option.dataset.price ||
        '0',
        10
      );

      if (priceElement) {
        priceElement.textContent =
          this.formatMoney(price);
      }

      if (compareElement) {
        compareElement.textContent =
          this.formatMoney(comparePrice);

        compareElement.hidden =
          comparePrice <= price;
      }
    }

    updateBundle(bundle) {
      const products = this.getProducts(bundle);

      let selectedCount = 0;
      let total = 0;
      let compareTotal = 0;
      let hasUnavailableSelection = false;

      products.forEach((product) => {
        const checkbox = product.querySelector(
          SELECTORS.checkbox
        );

        const selected = checkbox
          ? checkbox.checked
          : product.classList.contains(
              'is-selected'
            );

        product.classList.toggle(
          'is-selected',
          selected
        );

        const data = this.getProductData(product);

        product.dataset.variantId =
          String(data.variantId || '');

        product.dataset.price =
          String(data.price || 0);

        product.dataset.comparePrice =
          String(
            data.comparePrice ||
            data.price ||
            0
          );

        product.dataset.available =
          data.available ? 'true' : 'false';

        if (!selected) return;

        selectedCount += 1;
        total += data.price;
        compareTotal += data.comparePrice;

        if (!data.available || !data.variantId) {
          hasUnavailableSelection = true;
        }
      });

      const totalElement = bundle.querySelector(
        SELECTORS.total
      );

      const compareElement = bundle.querySelector(
        SELECTORS.compare
      );

      const savingElement = bundle.querySelector(
        SELECTORS.saving
      );

      const countElement = bundle.querySelector(
        SELECTORS.selectedCount
      );

      const button = bundle.querySelector(
        SELECTORS.addButton
      );

      const saving = Math.max(
        0,
        compareTotal - total
      );

      if (totalElement) {
        totalElement.textContent =
          this.formatMoney(total);
      }

      if (compareElement) {
        compareElement.textContent =
          this.formatMoney(compareTotal);

        compareElement.hidden =
          compareTotal <= total;
      }

      if (savingElement) {
        savingElement.textContent =
          `Save ${this.formatMoney(saving)}`;

        savingElement.hidden =
          saving <= 0;
      }

      if (countElement) {
        countElement.textContent =
          String(selectedCount);
      }

      if (button) {
        const disabled =
          selectedCount < 2 ||
          hasUnavailableSelection;

        button.disabled = disabled;

        button.classList.toggle(
          'is-disabled',
          disabled
        );

        button.setAttribute(
          'aria-disabled',
          disabled ? 'true' : 'false'
        );
      }

      if (selectedCount < 2) {
        this.showValidation(
          bundle,
          'Select at least two products to create your bundle.'
        );
      } else if (hasUnavailableSelection) {
        this.showValidation(
          bundle,
          'Choose an available variant for every selected product.'
        );
      } else {
        this.clearValidation(bundle);
      }
    }

    showValidation(bundle, text) {
      let message = bundle.querySelector(
        '[data-ae-bundle-validation]'
      );

      const button = bundle.querySelector(
        SELECTORS.addButton
      );

      if (!message) {
        message = document.createElement('p');

        message.className =
          'ae-cart-bundle__minimum-message';

        message.dataset.aeBundleValidation = '';

        button?.insertAdjacentElement(
          'afterend',
          message
        );
      }

      message.textContent = text;
    }

    clearValidation(bundle) {
      bundle
        .querySelector(
          '[data-ae-bundle-validation]'
        )
        ?.remove();
    }

    showMessage(bundle, text, isError = false) {
      const message = bundle.querySelector(
        SELECTORS.message
      );

      if (!message) return;

      message.hidden = false;
      message.textContent = text;

      message.classList.toggle(
        'is-error',
        isError
      );
    }

    setLoading(button, loading) {
      button.disabled = loading;

      button.classList.toggle(
        'is-loading',
        loading
      );

      button.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );
    }

    async addBundle(button) {
      const bundle = button.closest(
        SELECTORS.bundle
      );

      if (
        !bundle ||
        button.classList.contains('is-loading')
      ) {
        return;
      }

      const selectedProducts =
        this.getSelectedProducts(bundle);

      const items = selectedProducts
        .map((product) =>
          this.getProductData(product)
        )
        .filter((data) => {
          return (
            data.available &&
            Number.isFinite(data.variantId) &&
            data.variantId > 0
          );
        })
        .map((data) => ({
          id: data.variantId,
          quantity: 1
        }));

      if (items.length < 2) {
        this.showMessage(
          bundle,
          'Select at least two available products.',
          true
        );

        return;
      }

      this.setLoading(button, true);
      this.showMessage(bundle, '', false);

      try {
        const response = await fetch(
          this.getCartAddUrl(),
          {
            method: 'POST',

            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },

            body: JSON.stringify({
              items
            })
          }
        );

        const data = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.description ||
            data.message ||
            'The bundle could not be added.'
          );
        }

        this.showMessage(
          bundle,
          'Bundle added successfully.'
        );

        await this.refreshCartDrawer();
      } catch (error) {
        console.error(
          '[Assaboor Bundle]',
          error
        );

        this.showMessage(
          bundle,
          error instanceof Error
            ? error.message
            : 'The bundle could not be added.',
          true
        );

        this.setLoading(button, false);
        this.updateBundle(bundle);
      }
    }

    getCartAddUrl() {
      if (window.routes?.cart_add_url) {
        return `${window.routes.cart_add_url}.js`
          .replace('.js.js', '.js');
      }

      const root =
        window.Shopify?.routes?.root || '/';

      return `${root}cart/add.js`;
    }

    async refreshCartDrawer() {
      const currentShell =
        document.querySelector(
          SELECTORS.drawerShell
        );

      if (!currentShell) {
        window.location.reload();
        return;
      }

      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        'section_id',
        'ae-cart-drawer'
      );

      const response = await fetch(
        url.toString(),
        {
          headers: {
            Accept: 'text/html'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to refresh the cart drawer.'
        );
      }

      const html = await response.text();

      const parsedDocument =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const newShell =
        parsedDocument.querySelector(
          SELECTORS.drawerShell
        );

      if (!newShell) {
        throw new Error(
          'Updated cart drawer markup was not found.'
        );
      }

      currentShell.replaceWith(newShell);

      const drawer = document.querySelector(
        SELECTORS.drawer
      );

      const overlay = document.querySelector(
        SELECTORS.overlay
      );

      drawer?.classList.add('is-open');

      drawer?.setAttribute(
        'aria-hidden',
        'false'
      );

      overlay?.classList.add('is-open');

      document.documentElement.classList.add(
        'ae-cart-drawer-open'
      );

      this.refreshAllBundles();

      document.dispatchEvent(
        new CustomEvent(
          'ae:cart:updated'
        )
      );

      document.dispatchEvent(
        new CustomEvent(
          'cart:updated'
        )
      );
    }

    formatMoney(cents) {
      const value = Number.isFinite(cents)
        ? cents
        : 0;

      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(
          value,
          window.Shopify.money_format ||
          '${{amount}}'
        );
      }

      const currency =
        window.Shopify?.currency?.active ||
        document.documentElement.dataset
          .currency ||
        'USD';

      return new Intl.NumberFormat(
        document.documentElement.lang || 'en',
        {
          style: 'currency',
          currency
        }
      ).format(value / 100);
    }
  }

  const initialize = () => {
    if (window.AECartBundleInstance) {
      window.AECartBundleInstance
        .refreshAllBundles();

      return;
    }

    window.AECartBundleInstance =
      new AECartBundle();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();